import { isLoggedOnSelector } from '../recoil/selectors/isLoggedOnSelector';
import { OnlineSalesUnauthenticated } from 'gw-edge-client';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    type AddCoveragePriceDTO,
    type AddCoverageDTO,
    type RemoveCoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useEffect, useState } from 'react';
import { useOpcInfomation } from './useOpcInfomation';
import { convertDateToLocalDateDTO } from '../utils/dateUtil';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { isOpcFetchingAtom } from '../recoil/atoms/isOpcFetchingAtom';
import { jobAtom } from '../recoil/atoms/jobAtom';
import { DRAFT } from '../recoil/types/types';

export function useAddCoverageOPC(): {
    addCoveragePriceDTO: AddCoveragePriceDTO | undefined;
    addCoverageDTO: AddCoverageDTO;
    revertChange: () => Promise<void>;
    updateCoverageDate: (date: Date) => void;
} {
    const { jobNumber, sessionId } = useRecoilValue(jobInfoAtom);
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const setIsOpcFetching = useSetRecoilState(isOpcFetchingAtom);
    const { addRemoveCoverage } = useOpcInfomation();
    const { productName, coverableId, coverageId } = addRemoveCoverage;
    const [addCoverageDTO, setAddCoverageDTO] = useState<AddCoverageDTO>({
        coverableId,
        productName,
        coverageId
    });

    const [addCoveragePriceDTO, setAddCoveragePriceDTO] = useState<AddCoveragePriceDTO | undefined>(
        undefined
    );

    if (!jobNumber) {
        throw new Error('Invalid jobNumber');
    }
    if (!sessionId) {
        throw new Error('Invalid sessionId');
    }

    useEffect(() => {
        const calculateAddCoveragePriceForOPC = async (): Promise<void> => {
            setIsOpcFetching(true);
            await OnlineSalesUnauthenticated.calculateAddCoveragePriceForOPC(
                jobNumber,
                sessionId,
                addCoverageDTO,
                !isLoggedOn
            )
                .then((res) => {
                    setAddCoveragePriceDTO(res);
                    setJobData({ ...jobData, jobStatus: DRAFT });
                })
                .catch(() => {
                    return undefined;
                })
                .finally(() => {
                    setIsOpcFetching(false);
                });
        };
        void calculateAddCoveragePriceForOPC();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addCoverageDTO]);

    const revertChange = async (): Promise<void> => {
        setIsOpcFetching(true);
        const removeCoverageDTO: RemoveCoverageDTO = {
            ...addCoverageDTO,
            isUpdateOnly: false
        };
        await OnlineSalesUnauthenticated.calculateRemoveCoveragePriceForOPC(
            jobNumber,
            sessionId,
            removeCoverageDTO,
            !isLoggedOn
        )
            .catch(() => {
                return undefined;
            })
            .finally(() => {
                setIsOpcFetching(false);
            });
    };

    const updateCoverageDate = (date: Date): void => {
        setAddCoverageDTO((prev) => ({
            ...prev,
            effectiveDate: convertDateToLocalDateDTO(date)
        }));
    };

    return { addCoveragePriceDTO, addCoverageDTO, updateCoverageDate, revertChange };
}
